import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DataLoadingError from '@components/common/DataLoadingError';
import useCurriculaQuery from '@components/common/hooks/api/useCurriculaQuery';
import useEventDetailsQuery from '@components/common/hooks/api/useEventDetailsQuery';
import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import { axiosRequest } from '@lib/axios';
import { AbilityContext, Subjects } from '@lib/permissions';
import { scrollToAlert } from '@lib/utils';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import EventForm from '../EventForm';
import useModifiedModal from './useModifiedModal';

const EditEvent = () => {
  const [submitting, setSubmitting] = useState(false);
  const valuesSetState = useState(null);

  const { setBreadcrumbValue, setPageMessage, setPageErrorMessage } = useContext(PageContext);
  const { userRecord } = useContext(UserContext);

  const ability = useAbility(AbilityContext);

  const { eventID } = useParams();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const modifiedModal = useModifiedModal();

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      point: null,
      event_type: null,
      additional: null,
      curriculum: null,
      participant_needs: null,
      program_areas: null,
      created_by: null,
      event_name: null,
      event_partner: null,
      main_office: null,
      special_project: null,
      is_test: null,
      teen_parents: null,
      description: 'Event description coming in a future phase.',
    }
  });

  const staffUsersQuery = useStaffUsersQuery();
  const pearsQuery = usePearsQuery();
  const curriculaQuery = useCurriculaQuery();
  const { data: event, ...eventQuery } = useEventDetailsQuery(eventID);

  const deleteSessionsMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'DELETE',
      url: `/events/${eventID}/sessions`,
      data,
    }),
    onError: (err) => {
      console.log('[EditEvent][deleteSessions] error:', err);
      setSubmitting(false);
      setPageErrorMessage(err);
      scrollToAlert();
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'PUT',
      url: `/events/${eventID}`,
      data,
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      setSubmitting(false);
      await eventQuery.refetch();
      await Promise.all([
        // Need to clear cached session queries here to fix display issues on pages like Complete Session
        // Technically, we only need to clear cache for sessions related to this event, but it's simpler to clear all.
        queryClient.invalidateQueries({ queryKey: ['sessions'] }),
        queryClient.invalidateQueries({ queryKey: ['events'] }),
      ]);
      navigate(`/events/${event.id}`, { state: { message: 'Event updated' } });
    },
    onError: (err) => {
      console.log('[EditEvent][updateEvent] error:', err);
      setSubmitting(false);
      setPageErrorMessage(err);
      scrollToAlert();
    },
  });

  useEffect(() => {
    if (event) {
      setBreadcrumbValue(':event', event);
    }
    // eslint-disable-next-line
  }, [event]);

  /**
   * Submit handler.
   *
   * @param values
   */
  const onSubmit = async (values) => {
    setPageErrorMessage(null);
    setPageMessage(null);

    let destroySessions = false;

    if (event.sessions?.length > 0) {
      // Determine if any destructive modifications were made
      const triggerKeys = ['program_areas', 'event_type', 'curriculum'];
      const modifiedFields = Object.keys(values).reduce((acc, key) => {
        if (!triggerKeys.includes(key)) {
          return acc;
        }
        if (key === 'curriculum') {
          if (values[key]?.id && values[key].id !== event?.curriculum_id) {
            acc.push(key);
          }
        } else {
          if (JSON.stringify(values[key]) !== JSON.stringify(event[key])) {
            acc.push(key);
          }
        }
        return acc;
      }, []);

      if (modifiedFields.length > 0) {
        destroySessions = true;

        // Pop modal asking user to confirm deleting sessions
        const continueSave = await modifiedModal({ modifiedFields, values, event: event });
        if (!continueSave) {
          setSubmitting(false);
          return;
        } else if (continueSave === -1) {
          navigate(`/events/${event.id}`);
          return;
        } else {
          // save changes
        }
      }
    }
    setSubmitting(true);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'additional':
        case 'curriculum':
          if (value?.id) {
            acc[`${key}_id`] = value?.id || null;
          }
          break;
        // case 'description':
        //   acc[key] = value || 'Event description coming in a future phase.';
        //   break;
        case 'event_name':
          acc[key] = value.trim();
          break;
        case 'participant_needs':
        case 'program_areas':
        case 'created_by':
        case 'event_partner':
        case 'event_type':
        case 'main_office':
        case 'points':
        case 'special_project':
          acc[key] = value?.id || value;
          break;
        case 'participant_needs_other':
          if (values.participant_needs?.includes('other')) {
            acc[key] = value?.id || value;
          } else {
            acc[key] = null;
          }
          break;
        case 'teen_parents':
        case 'is_test':
          acc[key] = values[key] === 'true';
          break;
        default:
          break;
      }
      return acc;
    }, {});

    if (input.created_by !== userRecord.email && ability.cannot('assign', Subjects.EVENT)) {
      setSubmitting(false);
      setPageErrorMessage('You do not have permission to assign events to other staff members.');
      scrollToAlert();
      return false;
    }

    try {
      if (destroySessions) {
        await deleteSessionsMutation.mutateAsync();
      }
      updateEventMutation.mutate(input);
    } catch (error) {
      console.log('Error updating event: ', error);
      setSubmitting(false);
      setPageErrorMessage('Error updating event. Please try again.', error);
      scrollToAlert();
    }
  };

  if (eventQuery.isLoading || pearsQuery.isLoading || staffUsersQuery.isLoading || curriculaQuery.isLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (eventQuery.isError && eventQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load event!'} errors={eventQuery.error.errors} />;
  }

  if (!event) {
    return (
      <>You do not have access, or the event was not found.</>
    );
  }

  if (ability.cannot('edit', subject(Subjects.EVENT, event))) {
    return (
      <>You do not have permission to edit this event.</>
    );
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <EventForm
          form={form}
          event={event}
          pearsData={pearsQuery.data}
          staffUsers={staffUsersQuery.data}
          availableCurriculum={curriculaQuery.data}
          submitting={submitting}
          onSubmit={onSubmit}
          valuesSetState={valuesSetState}
        />
      </Grid>
    </Grid>
  );
};

export default EditEvent;
